import { differenceInCalendarDays } from 'date-fns';
import { isEmpty, isNumber } from 'lodash';
import type { Dispatch as ReactDispatch } from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import type { Action } from '../../../hooks/useStateReducer';
import type { ExcludePlayerResponse } from '../definitions';
import { toTimestamp } from '../utils';

interface SelfExcludedData {
  daysRequested: number;
  minRevocationWaitPeriodDays: number;
  revocationWaitPeriodDays: number;
  selfExcludedInfoFlags: number;
  timeUntil: number;
  banEndDate: number;
  timeFrom: number;
}

const useSelfExclusionData = (
  response?: ExcludePlayerResponse,
  dispatchAction?: ReactDispatch<Action<ExcludePlayerResponse>>,
  preventFetch = false
) => {
  const request = useRequest();
  const [daysLeft, setDaysLeft] = useState(0);
  const [selfExcluded, setSelfExcluded] = useState(false);
  const [selfExcludedDataFetched, setSelfExcludedDataFetched] = useState(false);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const [selfExclusionData, setSelfExclusionData] = useState<SelfExcludedData>();

  useEffect(() => {
    if (isAuthenticated && !preventFetch) {
      request({ service: 'responsiblegaming.getselfexcludedinfo' }).subscribe({
        next: ({ body }) => {
          if (!isEmpty(body)) {
            const timeUntil = toTimestamp(body.timeUntil);
            const timeFrom = toTimestamp(body.timeFrom);

            setSelfExcluded(isNumber(body.daysRequested));
            setSelfExclusionData({ ...body, timeFrom, timeUntil });
            setDaysLeft(differenceInCalendarDays(timeUntil, timeFrom));
          }
          setSelfExcludedDataFetched(true);
        },
        error: () => setSelfExcludedDataFetched(true)
      });
    }
  }, [dispatchAction, request, response, isAuthenticated, preventFetch]);

  return { selfExcluded, selfExclusionData, daysLeft, selfExcludedDataFetched };
};

export default useSelfExclusionData;
